import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Slider, InputAdornment, TextField } from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { InputMaskHeight } from '../../Helpers/Formatters';
import { useController, useFormContext } from 'react-hook-form';
import './SliderInput.scss';

// Memoize the wrapper so unrelated form state changes do not repaint this slider/input pair.
const HeightInput = memo((props) => {
    const { variant, name, id, label, value, onChange, min, max, className, openEditPopUp } = props;
    const formContext = useFormContext();
    // Use the parent FormProvider control; creating a fallback form during render adds avoidable hook work.
    const { control } = formContext;
    const {
        field,       // Contains: onChange, onBlur, value, ref
        fieldState: { error }   // Contains: error
    } = useController({ name, control });
    const [height, setHeight] = useState(value);

    // Keep the local masked value in sync when the parent resets or hydrates the form.
    useEffect(() => {
        setHeight(value);
    }, [value]);

    const formatHeightValue = useCallback((heightInInches) => {
        const feet = Math.floor(heightInInches / 12);
        const inches = heightInInches % 12;
        return `${feet} ft ${inches.toString().padStart(2, '0')} in`;
    }, []);

    const convertHeightToInches = useCallback((heightStr) => {
        const [feet, inches] = heightStr.split('ft').map((part) => part.trim());
        const feetInInches = (feet && Number(feet) * 12) || 0;
        const inchesValue = (inches && Number(inches.replace('in', '').trim())) || 0;
        return feetInInches + inchesValue;
    }, []);

    const handleSliderChange = useCallback((event, newValue) => {
        const formattedHeight = formatHeightValue(newValue);
        setHeight(formattedHeight);
        field.onChange(formattedHeight);
        onChange(formattedHeight);
    }, [field, formatHeightValue, onChange]);

    const heightInInches = useMemo(() => convertHeightToInches(height || ''), [convertHeightToInches, height]);

    // Render only endpoint marks; step=1 still supports every inch without creating 100+ mark nodes.
    const heightMarks = useMemo(() => [
        { value: min, label: `${Math.floor(min / 12)}ft` },
        { value: max, label: `${Math.floor(max / 12)}ft` },
    ], [max, min]);

    // Stable adornment and slot props avoid remounting the masked input on parent renders.
    const endAdornment = useMemo(() => (
        <InputAdornment position="end" className="text-input-carrot" disablePointerEvents>
            <ArrowDropDownIcon />
        </InputAdornment>
    ), []);

    const slotProps = useMemo(() => ({
        input: {
            inputComponent: InputMaskHeight,
            endAdornment,
        },
        inputLabel: {
            shrink: height ? true : undefined,
        },
    }), [endAdornment, height]);

    const handleTextChange = useCallback((e) => {
        // Update local display and RHF/parent state in one lightweight change handler.
        const newValue = e.target.value;
        setHeight(newValue);
        field.onChange(newValue);
        onChange(newValue);
    }, [field, onChange]);

    return (
        <div className='slider-input-container'>
            <TextField
                variant={variant || 'outlined'}
                name={name}
                id={id}
                className={className}
                label={label}
                value={height}
                error={!!error || (openEditPopUp && !height)}
                helperText={error?.message}
                onChange={handleTextChange}
                slotProps={slotProps} />
            <div className="slider-container">
                <div className="me-2 slider-div">
                    <Slider
                        min={min}
                        max={max}
                        // Keep one-inch precision without forcing MUI to render a mark for every value.
                        step={1}
                        value={heightInInches}
                        onChange={handleSliderChange}
                        className="m-2"
                        sx={{ mb: '10px' }}
                        marks={heightMarks}
                        tabIndex={-1}
                    />
                </div>
            </div>
        </div>
    );
});

HeightInput.propTypes = {
    variant: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    className: PropTypes.string,
    openEditPopUp: PropTypes.bool,
};

export default HeightInput;
